import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";

const LOGO_URL = "/rumeli-logo.png";

const navLinks = [
  { label: "Ana Sayfa", path: "/" },
  { label: "Hakkımızda", path: "/hakkimizda" },
  { label: "Branşlarımız", path: "/branslarimiz" },
  { label: "Antrenörler", path: "/antrenorler" },
  { label: "Yaz Spor Okulu", path: "/yaz-spor-okulu" },
  { label: "Tesisler", path: "/tesisler" },
  { label: "İletişim", path: "/iletisim" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/95 backdrop-blur-xl shadow-md"
          : "bg-white/80 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
            <img src={LOGO_URL} alt="Rumeli Üniversitesi" className="h-10 lg:h-12 w-auto" />
            <div>
              <p className="font-heading text-sm lg:text-base font-bold text-burgundy uppercase tracking-wider leading-tight">
                İRÜ Spor Kulübü
              </p>
              <p className="text-[10px] lg:text-xs text-gray-500 font-accent tracking-wide hidden sm:block">
                Rumeli Üniversitesi Bostancı Yerleşkesi
              </p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.path.replace("/", "") || "home"}`}
                className={`px-3 py-2 text-xs font-heading uppercase tracking-widest transition-colors duration-200 ${
                  isActive(link.path)
                    ? "text-burgundy border-b-2 border-burgundy"
                    : "text-gray-700 hover:text-burgundy"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a href="tel:05436470183" className="flex items-center gap-1 text-xs text-gray-600 hover:text-burgundy transition-colors">
              <Phone className="w-3.5 h-3.5" strokeWidth={1.5} />
              <span className="font-accent">0543 647 01 83</span>
            </a>
            <Link to="/on-kayit">
              <Button
                data-testid="nav-register-btn"
                className="bg-burgundy hover:bg-burgundy-700 text-white text-xs font-heading uppercase tracking-wider px-6"
              >
                Ön Kayıt
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] bg-white">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex flex-col pt-8 gap-1">
                <div className="flex items-center gap-3 mb-8 px-2">
                  <img src={LOGO_URL} alt="Rumeli Üniversitesi" className="h-10 w-auto" />
                  <div>
                    <p className="font-heading text-sm font-bold text-burgundy uppercase tracking-wider">İRÜ Spor Kulübü</p>
                  </div>
                </div>
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${link.path.replace("/", "") || "home"}`}
                    className={`px-4 py-3 text-sm font-heading uppercase tracking-wider transition-colors ${
                      isActive(link.path)
                        ? "text-burgundy bg-burgundy-50"
                        : "text-gray-700 hover:text-burgundy hover:bg-gray-50"
                    }`}
                  >
                    {link.label}
                  </Link>
                ))}
                <div className="mt-6 px-4">
                  <Link to="/on-kayit" onClick={() => setOpen(false)}>
                    <Button
                      data-testid="mobile-register-btn"
                      className="w-full bg-burgundy hover:bg-burgundy-700 text-white font-heading uppercase tracking-wider"
                    >
                      Ön Kayıt
                    </Button>
                  </Link>
                </div>
                <div className="mt-4 px-4">
                  <a href="tel:05436470183" className="flex items-center gap-2 text-sm text-gray-600">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    <span className="font-accent">0543 647 01 83</span>
                  </a>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  );
}
